/**
 * ScoreDeltaChip - a default Carbon tag showing a Guardian Score delta.
 *
 * Hidden when delta is null/undefined or below the rounding-zero noise
 * threshold (|delta| < 0.05). Format: "+0.5 points" / "-0.5 points" up to
 * 9.9, "+12 points" / "-12 points" at 10+. "points", not the "GS"
 * abbreviation, which no reader decodes.
 *
 * No hue by sign (the Neutral Bad State Rule): the sign carries the
 * direction, and the tag is the same neutral in both directions. The
 * explanation is a hover tooltip that describes the chip; it never becomes a
 * control, because the chip often sits inside a clickable row.
 */

import { Tag } from '@/components/carbon/Tag'
import { Tooltip } from '@/components/carbon/Tooltip'

/** Deltas below this render nothing — exported so other score-impact surfaces
 *  (e.g. the triage dossier's "+2.1 points" line) share the same noise floor. */
export const NOISE_THRESHOLD = 0.05
const INTEGER_BOUNDARY = 10

/**
 * Format a delta into the chip string. Exported for unit-test reuse.
 *
 *    0.5   -> "+0.5 points"
 *   -0.4   -> "-0.4 points"
 *    9.95  -> "+10 points"   (rounded into integer band)
 *  -12.34  -> "-12 points"
 *
 * Returns null when the chip should be hidden.
 */
export function formatScoreDelta(delta: number | null | undefined): string | null {
  if (delta == null) return null
  if (Math.abs(delta) < NOISE_THRESHOLD) return null
  const abs = Math.abs(delta)
  const magnitude = abs >= INTEGER_BOUNDARY
    ? Math.round(abs).toString()
    : abs.toFixed(1)
  const sign = delta < 0 ? '-' : '+'
  return `${sign}${magnitude} points`
}

export interface ScoreDeltaChipProps {
  /** Guardian Score delta in percentage points. Null/undefined or near-zero
   *  values render nothing. */
  delta: number | null | undefined
  /** Optional override of the default tooltip copy, primarily for testing. */
  tooltipOverride?: string
  /** Kept for callers that render the chip inside another interactive
   *  element. The tooltip now only describes the chip (aria-describedby) and
   *  never replaces its text as the accessible name, so the chip text always
   *  carries the name; this prop has no further effect. */
  omitAriaLabel?: boolean
}

export default function ScoreDeltaChip({ delta, tooltipOverride }: ScoreDeltaChipProps) {
  const label = formatScoreDelta(delta)
  if (label == null || delta == null) return null

  // One-decimal display for the tooltip so "0.50" doesn't surface as "1"
  // even when the chip itself uses the integer band.
  const points = Math.abs(delta).toFixed(Math.abs(delta) >= INTEGER_BOUNDARY ? 0 : 1)
  // The lift is arithmetic on the framework weighted average — driven by
  // the policy's weight in its framework, not by its SHALL/SHOULD tier.
  // Tooltip frames it conversationally so two policies of similar weight
  // (one SHALL, one SHOULD) reading as the same lift doesn't surprise.
  const tooltip = tooltipOverride ?? (
    delta < 0
      ? `Your Guardian Score dropped ~${points} points. The math is weighted by each policy's framework weight, not its SHALL/SHOULD tier.`
      : `Closing this nudges your Guardian Score up by ~${points} points. The number comes from this policy's weight in the framework, so a SHOULD and a SHALL of similar weight can show the same lift.`
  )

  return (
    <Tooltip interactive={false} label={tooltip} align="start">
      <Tag className="tabular-nums">{label}</Tag>
    </Tooltip>
  )
}
